import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import CustomNavbar from "./CustomPermissions/CustomNavbar.jsx";
import { getApiUrl, API_ENDPOINTS, API_BASE_URL } from "../config/api";
import { exportRecordsToExcel } from "../utils/exportUtils";

function Records() {
  const [data, setData] = useState(null);
  const [dailyEntryData, setDailyEntryData] = useState([]);
  const [isSuperuser, setIsSuperuser] = useState(false); // Track if user is superuser (for delete permission)
  const [pipelineandturnedup, setPipelineandturnedup] = useState([]);
  const [candidateRecords, setCandidateRecords] = useState({}); // Store fetched candidate records per user
  const [activeFilter, setActiveFilter] = useState({}); // Track active filter (resume_forwarded, pipeline, turned_up)
  const navigate = useNavigate();
  const [uniquePipelineCounts, setUniquePipelineCounts] = useState({});
  const [deleteConfirmUser, setDeleteConfirmUser] = useState(null); // Track user to delete
  const [updateTargetUser, setUpdateTargetUser] = useState(null); // Track user to update target
  const [targetInput, setTargetInput] = useState('');
  const [revenueTargetInput, setRevenueTargetInput] = useState('');
  const [displayMode, setDisplayMode] = useState('target_candidates'); // 'target_candidates' or 'revenue'
  const [editUser, setEditUser] = useState(null); // Track user to edit
  const [editName, setEditName] = useState('');
  const [editDesignation, setEditDesignation] = useState('');
  const [editImage, setEditImage] = useState(null);
  const [designations, setDesignations] = useState([]);

  // Handle permission-based navigation
  const handlePermissionNavigation = (userPermissions) => {
    if (userPermissions.includes("website.custom_viewer_perm")) {
      navigate("/viewers", {
        replace: true,
        state: { userPermissions: "custom_viewer_perm" },
      });
    }
  };

  // Fetch display mode settings
  useEffect(() => {
    const fetchDisplayMode = async () => {
      try {
        const accessToken = localStorage.getItem("access_token");
        if (accessToken) {
          const response = await axios.get(
            getApiUrl('/display-mode-settings/'),
            {
              headers: {
                Authorization: `Bearer ${accessToken}`,
              },
            }
          );
          setDisplayMode(response.data.pace_display_mode || 'target_candidates');
        }
      } catch (error) {
        console.error("Error fetching display mode:", error);
      }
    };
    fetchDisplayMode();
  }, []);

  // Fetch designations for edit form
  useEffect(() => {
    const fetchDesignations = async () => {
      try {
        const accessToken = localStorage.getItem("access_token");
        if (accessToken) {
          const response = await axios.get(
            getApiUrl('/designations/'),
            {
              headers: {
                Authorization: `Bearer ${accessToken}`,
              },
            }
          );
          setDesignations(response.data || []);
        }
      } catch (error) {
        console.error("Error fetching designations:", error);
      }
    };
    fetchDesignations();
  }, []);

  // Fetch initial data on component mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        const accessToken = localStorage.getItem("access_token");
        if (accessToken) {
          const response = await axios.get(
            getApiUrl(API_ENDPOINTS.GET_ALL_RECORDS),
            {
              headers: {
                Authorization: `Bearer ${accessToken}`,
              },
            }
          );
          const {
            records,
            monthly_records,
            is_superuser,
            user_permissions,
            pipelineandturnedup,
          } = response.data;
          setData(records || []);
          setDailyEntryData(monthly_records || []);
          setIsSuperuser(is_superuser);
          setPipelineandturnedup(pipelineandturnedup || []);

          // Extract unique pipeline counts from backend response (don't recalculate)
          const uniquePipelineCountsMap = {};
          monthly_records.forEach(entry => {
            uniquePipelineCountsMap[entry.user] = entry.pipeline_unique || 0;
          });

          setUniquePipelineCounts(uniquePipelineCountsMap); // Store unique counts from backend

          if (is_superuser) {
            // Superuser logic
          } else if (user_permissions) {
            handlePermissionNavigation(user_permissions);
          }
        }
      } catch (error) {
        console.error("There was a problem with the fetch operation:", error);
      }
    };
    fetchData();
  }, [navigate]);

  // Function to fetch candidate records for a specific user with filter
  const fetchCandidateRecords = async (userId, filterType) => {
    try {
      const accessToken = localStorage.getItem("access_token");

      // Get current month and year
      const currentDate = new Date();
      const currentMonth = currentDate.getMonth() + 1; // JavaScript months are 0-indexed
      const currentYear = currentDate.getFullYear();

      const response = await axios.get(
        getApiUrl(`/GetAllCandidateOfUser/${userId}/`),
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
          params: {
            status: filterType,
            month: currentMonth,
            year: currentYear,
            include_active: 'true' // Include pipeline/turned_up from any month
          },
        }
      );

      // Filter records on frontend as well to ensure proper filtering
      let records = response.data.data || [];

      // Apply filtering logic:
      // If filtering by a specific status (not "all"), show ONLY that status
      if (filterType && filterType !== 'all') {
        // For backout, selected, not_selected, joined: show only current month
        if (['backout', 'selected', 'not_selected', 'joined'].includes(filterType)) {
          records = records.filter(record => {
            const recordDate = new Date(record.date_entered);
            const isCurrentMonth = recordDate.getMonth() === currentDate.getMonth() &&
              recordDate.getFullYear() === currentDate.getFullYear();
            return record.status === filterType && isCurrentMonth;
          });
        } else {
          // For pipeline, turned_up, resume_forwarded: show from any month
          records = records.filter(record => record.status === filterType);
        }
      } else {
        // When showing "all" records:
        // 1. Show ALL current month records regardless of status
        // 2. Show pipeline/turned_up/resume_forwarded from ANY month (active candidates)
        records = records.filter(record => {
          const recordDate = new Date(record.date_entered);
          const isCurrentMonth = recordDate.getMonth() === currentDate.getMonth() &&
            recordDate.getFullYear() === currentDate.getFullYear();

          // Show if it's current month OR if it's an active status
          const isActiveStatus = ['pipeline', 'turned_up', 'resume_forwarded'].includes(record.status);

          return isCurrentMonth || isActiveStatus;
        });
      }

      return records;
    } catch (error) {
      console.error("Error fetching candidate records:", error);
      return [];
    }
  };

  const [expandedRows, setExpandedRows] = useState(null); // Track only one expanded row at a time
  const [nestedExpandedRows, setNestedExpandedRows] = useState({}); // Track nested expanded rows per user

  // Toggle expanded row for a specific user and filter type
  const toggleExpandedRow = async (userId, filterType) => {
    if (expandedRows === userId && activeFilter[userId] === filterType) {
      // If already expanded with the same filter, collapse it
      setExpandedRows(null);
      setActiveFilter((prev) => ({ ...prev, [userId]: null }));
    } else {
      // Fetch records for the clicked filter type (on demand)
      const records = await fetchCandidateRecords(userId, filterType);
      setCandidateRecords((prev) => ({ ...prev, [userId]: records }));

      setExpandedRows(userId); // Set the clicked row as expanded
      setActiveFilter((prev) => ({ ...prev, [userId]: filterType }));
    }
  };

  // Toggle nested expanded row for a specific record
  const toggleNestedExpandedRow = (userId, recordId) => {
    setNestedExpandedRows((prev) => {
      const updatedNestedRows = { ...prev };
      if (updatedNestedRows[userId] === recordId) {
        // If the same nested row is clicked, collapse it
        delete updatedNestedRows[userId];
      } else {
        // Collapse any previously expanded nested row and expand the clicked row
        updatedNestedRows[userId] = recordId;
      }
      return updatedNestedRows;
    });
  };

  // Handle delete user
  const handleDeleteUser = async (userId) => {
    try {
      const accessToken = localStorage.getItem("access_token");
      const response = await axios.delete(
        getApiUrl(`/DelRecord/${userId}/`),
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );

      // Refresh the data after deletion
      window.location.reload();
    } catch (error) {
      console.error("Error deleting user:", error);
      alert(error.response?.data?.message || "Error deleting user. Please try again.");
    }
    setDeleteConfirmUser(null);
  };

  // Handle update target
  const handleUpdateTarget = async () => {
    try {
      const accessToken = localStorage.getItem("access_token");
      const response = await axios.put(
        getApiUrl(`/UpdateEmployeeTarget/${updateTargetUser.id}/`),
        {
          target: parseInt(targetInput),
          revenue_target: parseFloat(revenueTargetInput)
        },
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
            'Content-Type': 'application/json'
          },
        }
      );

      alert(response.data.message || 'Target updated successfully!');
      setUpdateTargetUser(null);
      // Refresh the data after update
      window.location.reload();
    } catch (error) {
      console.error("Error updating target:", error);
      alert(error.response?.data?.message || "Error updating target. Please try again.");
    }
  };

  // Open update target modal
  const openUpdateTargetModal = (userId, userName, currentTarget, currentRevenue) => {
    setUpdateTargetUser({ id: userId, name: userName });
    setTargetInput(currentTarget || '');
    setRevenueTargetInput(currentRevenue || '');
  };

  // Handle display mode change
  const handleDisplayModeChange = async (newMode) => {
    try {
      const accessToken = localStorage.getItem("access_token");
      const response = await axios.put(
        getApiUrl('/display-mode-settings/update/'),
        {
          pace_display_mode: newMode
        },
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
            'Content-Type': 'application/json'
          },
        }
      );

      setDisplayMode(newMode);
      alert(response.data.message || 'Display mode updated successfully!');
    } catch (error) {
      console.error("Error updating display mode:", error);
      alert(error.response?.data?.message || "Error updating display mode. Please try again.");
    }
  };

  // Open edit user modal
  const openEditUserModal = (userId, userName, userDesignation) => {
    setEditUser({ id: userId, name: userName });
    setEditName(userName || '');
    setEditDesignation(userDesignation || '');
    setEditImage(null);
  };

  // Handle edit user details
  const handleEditUser = async () => {
    try {
      const accessToken = localStorage.getItem("access_token");
      const formData = new FormData();

      if (editName) formData.append('name', editName);
      if (editDesignation) formData.append('designation', editDesignation);
      if (editImage) formData.append('image', editImage);

      const response = await axios.put(
        getApiUrl(`/UpdateEmployeeDetails/${editUser.id}/`),
        formData,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
            'Content-Type': 'multipart/form-data'
          },
        }
      );

      alert(response.data.message || 'Employee details updated successfully!');
      setEditUser(null);
      // Refresh the data after update
      window.location.reload();
    } catch (error) {
      console.error("Error updating employee details:", error);
      alert(error.response?.data?.message || "Error updating employee details. Please try again.");
    }
  };

  // Sort data based on various criteria and display mode
  const sortedData = useMemo(() => {
    if (!dailyEntryData || !pipelineandturnedup || !data) return [];
    return dailyEntryData
      .map((entry) => {
        const pipeline =
          pipelineandturnedup.find((item) => item.user === entry.user)
            ?.pipline || 0;
        const turnedUpRatio =
          entry.turned_up && pipeline
            ? ((entry.selected + entry.turned_up) / entry.resume_forwarded) *
            100
            : 0;
        const correspondingRecord = data.find(
          (record) => record.id === entry.user
        );
        const targetRev = correspondingRecord ? correspondingRecord.revenue : 0;
        const target = correspondingRecord ? correspondingRecord.target : 0;
        return {
          ...entry,
          pipeline,
          turnedUpRatio,
          targetRev,
          target,
        };
      })
      .sort((a, b) => {
        // Sort based on display mode
        const bExp = isNaN(b.expected_revenue) ? 0 : parseFloat(b.expected_revenue);
        const aExp = isNaN(a.expected_revenue) ? 0 : parseFloat(a.expected_revenue);
        const bRev = isNaN(b.revenue) ? 0 : parseFloat(b.revenue);
        const aRev = isNaN(a.revenue) ? 0 : parseFloat(a.revenue);

        if (displayMode === 'revenue') {
          // Sort by expected revenue first when in revenue mode
          if (Math.abs(bExp - aExp) > 0.01) return bExp - aExp;
          if (Math.abs(bRev - aRev) > 0.01) return bRev - aRev;
          if (b.selected !== a.selected) return b.selected - a.selected;
          if (b.resume_forwarded !== a.resume_forwarded)
            return b.resume_forwarded - a.resume_forwarded;
          if (b.turnedUpRatio !== a.turnedUpRatio)
            return b.turnedUpRatio - a.turnedUpRatio;
          return b.pipeline - a.pipeline;
        } else {
          // Sort by target/candidates (selected) first when in target mode
          if (b.selected !== a.selected) return b.selected - a.selected;
          if (Math.abs(bExp - aExp) > 0.01) return bExp - aExp;
          if (Math.abs(bRev - aRev) > 0.01) return bRev - aRev;
          if (b.resume_forwarded !== a.resume_forwarded)
            return b.resume_forwarded - a.resume_forwarded;
          if (b.turnedUpRatio !== a.turnedUpRatio)
            return b.turnedUpRatio - a.turnedUpRatio;
          return b.pipeline - a.pipeline;
        }
      });
  }, [dailyEntryData, pipelineandturnedup, data, displayMode]);

  // Get accurate counts from backend monthly_records (single source of truth)
  const getAccurateCounts = (userId) => {
    const monthlyRecord = dailyEntryData.find(entry => entry.user === userId);

    if (!monthlyRecord) {
      return {
        resume_forwarded: 0,
        pipeline: 0,
        turned_up: 0,
        selected: 0,
        joined: 0,
        backout: 0,
        not_selected: 0,
        revenue: 0,
        expected_revenue: 0
      };
    }

    return {
      resume_forwarded: monthlyRecord.resume_forwarded || 0,
      pipeline: monthlyRecord.pipeline || monthlyRecord.pipline || 0,
      turned_up: monthlyRecord.turned_up || 0,
      selected: monthlyRecord.selected || 0,
      joined: monthlyRecord.joined || 0,
      backout: monthlyRecord.backout || 0,
      not_selected: monthlyRecord.not_selected || 0,
      revenue: monthlyRecord.revenue || 0,
      expected_revenue: monthlyRecord.expected_revenue || 0
    };
  };

  // Calculate totals for each column using accurate counts
  const totalForwarded = sortedData.reduce(
    (acc, curr) => acc + getAccurateCounts(curr.user).resume_forwarded,
    0
  );
  const totalPipeline = sortedData.reduce(
    (acc, curr) => acc + getAccurateCounts(curr.user).pipeline,
    0
  );
  const totalTurnedUp = sortedData.reduce(
    (acc, curr) => acc + getAccurateCounts(curr.user).turned_up,
    0
  );
  const totalSelected = sortedData.reduce(
    (acc, curr) => acc + getAccurateCounts(curr.user).selected,
    0
  );
  const totalJoined = sortedData.reduce(
    (acc, curr) => acc + getAccurateCounts(curr.user).joined,
    0
  );
  const totalBackout = sortedData.reduce(
    (acc, curr) => acc + getAccurateCounts(curr.user).backout,
    0
  );
  const totalNotSelected = sortedData.reduce(
    (acc, curr) => acc + getAccurateCounts(curr.user).not_selected,
    0
  );
  const totalRevenue = sortedData.reduce(
    (acc, curr) => acc + (isNaN(curr.revenue) ? 0 : parseFloat(curr.revenue)),
    0
  );
  const totalExpectedRevenue = sortedData.reduce(
    (acc, curr) => acc + (isNaN(curr.expected_revenue) ? 0 : parseFloat(curr.expected_revenue)),
    0
  );
  const totalTargetRevenue = sortedData.reduce((acc, curr) => {
    const correspondingRecord = data.find((record) => record.id === curr.user);
    return acc + (correspondingRecord?.revenue || 0);
  }, 0);
  const totalTargetCandidates = sortedData.reduce((acc, curr) => {
    const correspondingRecord = data.find((record) => record.id === curr.user);
    return acc + (correspondingRecord?.target || 0);
  }, 0);
  const totalPercentage = displayMode === 'target_candidates'
    ? (totalTargetCandidates > 0 ? (totalSelected / totalTargetCandidates) * 100 : 0)
    : (totalTargetRevenue > 0 ? (totalRevenue / totalTargetRevenue) * 100 : 0);

  // Helper function to filter unique pipelines based on hospital + location + speciality
  const getUniquePipelines = (records) => {
    const seenCombinations = new Set(); // To track unique combinations
    return records.filter((record) => {
      // Create unique key based on hospital, city, state, and speciality
      const combinationKey = `${record.hospital || 'unknown'}_${record.city || 'unknown'}_${record.state || 'unknown'}_${record.speciality || 'unknown'}`.toLowerCase();
      if (seenCombinations.has(combinationKey)) {
        return false; // Skip duplicate combination
      }
      seenCombinations.add(combinationKey); // Mark as seen
      return true; // Include unique record
    });
  };

  // Helper function to get total pipeline count (non-unique)
  const getTotalPipelineCount = (records) => {
    return records ? records.length : 0;
  };

  return (
    <>
      <section style={{ maxHeight: "100vh" }}>
        <div style={{ display: "flex" }}>
          <CustomNavbar />
          <div
            style={{
              width: "99vw",
              border: "1px solid #ddd",
              padding: "0 10px 10px 10px",
            }}
          >
            <div className="flex justify-between items-center mb-4" style={{ marginLeft: "30px", marginRight: "30px", marginTop: "10px" }}>
              <h2 className="text-blue-500 text-2xl font-bold">
                HR Associate's Records
              </h2>
              <div className="flex gap-4 items-center">
                {isSuperuser && (
                  <div className="flex items-center gap-2">
                    <label className="text-sm font-medium text-gray-700">Display Mode:</label>
                    <select
                      value={displayMode}
                      onChange={(e) => handleDisplayModeChange(e.target.value)}
                      className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                    >
                      <option value="target_candidates">Target & Candidates Closed</option>
                      <option value="revenue">Revenue Target & Revenue Generated</option>
                    </select>
                  </div>
                )}
                <button
                  onClick={() => {
                    const exportData = sortedData.map((entry) => {
                      const user = data.find((r) => r.id === entry.user);
                      const counts = getAccurateCounts(entry.user);
                      return {
                        name: user?.name || "",
                        username: user?.username || "",
                        resume_forwarded: counts.resume_forwarded,
                        pipeline_unique: uniquePipelineCounts[entry.user] || 0,
                        pipeline: counts.pipeline,
                        turned_up: counts.turned_up,
                        selected: counts.selected,
                        joined: counts.joined,
                        backout: counts.backout,
                        not_selected: counts.not_selected,
                        revenue: entry.revenue || 0,
                        expected_revenue: entry.expected_revenue || 0,
                        target: user?.target || 0,
                      };
                    });
                    exportRecordsToExcel(exportData, `HR_Records_${new Date().toISOString().split('T')[0]}`);
                  }}
                  className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2"
                >
                  📥 Export to Excel
                </button>
              </div>
            </div>
            <table style={{ width: "100%", backgroundColor: "white" }}>
              <thead>
                <tr
                  className="bg-blue-500"
                  style={{
                    position: "sticky",
                    top: "0",
                    fontSize: "16px",
                  }}
                >
                  <th className="py-2 px-4 border-b">Images</th>
                  <th className="py-2 px-4 border-b">Name</th>
                  <th className="py-2 px-4 border-b">Forwarded</th>
                  <th className="py-2 px-4 border-b">Pipeline</th>
                  <th className="py-2 px-4 border-b">Turned Up</th>
                  <th className="py-2 px-4 border-b">
                    {displayMode === 'target_candidates' ? 'Selected Candidate' : 'Selected This Month'}
                  </th>
                  <th className="py-2 px-4 border-b">Joined</th>
                  <th className="py-2 px-4 border-b">Backout</th>
                  <th className="py-2 px-4 border-b">Not Selected</th>
                  <th className="py-2 px-4 border-b">Exp. Revenue</th>
                  <th className="py-2 px-4 border-b">
                    {displayMode === 'target_candidates' ? 'Target / Achieved' : 'Revenue'}
                  </th>
                  <th className="py-2 px-4 border-b">Achievement Ratio</th>
                  {isSuperuser && <th className="py-2 px-4 border-b">Action</th>}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-300">
                {sortedData.map((dailyEntry) => {
                  const correspondingRecord = data.find(
                    (record) => record.id === dailyEntry.user
                  );
                  const imageSrc = correspondingRecord?.image
                    ? (correspondingRecord.image.startsWith('http')
                      ? correspondingRecord.image
                      : `${API_BASE_URL}${correspondingRecord.image}`)
                    : null;
                  const userName = correspondingRecord
                    ? correspondingRecord.name
                    : "";
                  const targetRev = correspondingRecord
                    ? correspondingRecord.revenue
                    : "";
                  const target = correspondingRecord
                    ? correspondingRecord.target
                    : "";
                  const pipeline =
                    pipelineandturnedup.find(
                      (entry) => entry.user === dailyEntry.user
                    )?.pipline || "";
                  const isExpanded = expandedRows === dailyEntry.user;

                  // Get accurate counts from filtered records
                  const accurateCounts = getAccurateCounts(dailyEntry.user);

                  return (
                    <React.Fragment key={dailyEntry.user}>
                      {/* Main Row */}
                      <tr style={{ fontSize: "14px" }}>
                        <td className="py-2 px-4 border-b text-center">
                          {imageSrc && (
                            <img
                              src={imageSrc}
                              className="w-auto"
                              style={{ height: "100px" }}
                              alt=""
                            />
                          )}
                        </td>
                        <td className="py-2 px-4 border-b text-center">
                          {userName}
                        </td>
                        <td
                          className="py-2 px-4 border-b text-center cursor-pointer text-blue-500"
                          onClick={() =>
                            toggleExpandedRow(
                              dailyEntry.user,
                              "resume_forwarded"
                            )
                          }
                        >
                          {accurateCounts.resume_forwarded}
                        </td>
                        <td
                          className="py-2 px-4 border-b text-center cursor-pointer text-blue-500"
                          onClick={() =>
                            toggleExpandedRow(dailyEntry.user, "pipeline")
                          }
                        >
                          {uniquePipelineCounts[dailyEntry.user] || 0} /{" "}
                          {accurateCounts.pipeline}
                        </td>
                        <td
                          className="py-2 px-4 border-b text-center cursor-pointer text-blue-500"
                          onClick={() =>
                            toggleExpandedRow(dailyEntry.user, "turned_up")
                          }
                        >
                          {accurateCounts.turned_up}
                        </td>
                        <td
                          className="py-2 px-4 border-b text-center cursor-pointer text-blue-500"
                          onClick={() =>
                            toggleExpandedRow(dailyEntry.user, "selected")
                          }
                        >
                          {displayMode === 'target_candidates'
                            ? `${accurateCounts.selected} / ${target}`
                            : accurateCounts.selected
                          }
                        </td>
                        <td
                          className="py-2 px-4 border-b text-center cursor-pointer text-blue-500"
                          onClick={() =>
                            toggleExpandedRow(dailyEntry.user, "joined")
                          }
                        >
                          {accurateCounts.joined}
                        </td>
                        <td
                          className="py-2 px-4 border-b text-center cursor-pointer text-blue-500"
                          onClick={() =>
                            toggleExpandedRow(dailyEntry.user, "backout")
                          }
                        >
                          {accurateCounts.backout}
                        </td>
                        <td
                          className="py-2 px-4 border-b text-center cursor-pointer text-blue-500"
                          onClick={() =>
                            toggleExpandedRow(dailyEntry.user, "not_selected")
                          }
                        >
                          {accurateCounts.not_selected}
                        </td>
                        <td className="py-2 px-4 border-b text-center">
                          {displayMode === 'target_candidates'
                            ? `${parseFloat(accurateCounts.expected_revenue).toFixed(2)} lac`
                            : `${parseFloat(accurateCounts.expected_revenue).toFixed(2)} / ${parseFloat(targetRev).toFixed(2)} lac`
                          }
                        </td>
                        <td className="py-2 px-4 border-b text-center">
                          {displayMode === 'target_candidates'
                            ? `${accurateCounts.selected} / ${target}`
                            : `${isNaN(dailyEntry.revenue) ? '0.00' : parseFloat(dailyEntry.revenue).toFixed(2)} lac`
                          }
                        </td>
                        <td
                          className={`py-2 px-4 border-b text-center ${(() => {
                            const percentage = displayMode === 'target_candidates'
                              ? (target > 0 ? (accurateCounts.selected / target) * 100 : 0)
                              : (targetRev > 0 ? (dailyEntry.revenue / targetRev) * 100 : 0);

                            if (percentage >= 100) return "text-blue-600";
                            if (percentage >= 95) return "text-green-500";
                            if (percentage >= 85) return "text-yellow-500";
                            return "text-red-500";
                          })()
                            }`}
                        >
                          {(() => {
                            const percentage = displayMode === 'target_candidates'
                              ? (target > 0 ? (accurateCounts.selected / target) * 100 : 0)
                              : (targetRev > 0 ? (dailyEntry.revenue / targetRev) * 100 : 0);

                            if (targetRev === 0 && displayMode === 'revenue') {
                              return <span className="text-red-500">0%</span>;
                            }
                            if (target === 0 && displayMode === 'target_candidates') {
                              return <span className="text-red-500">0%</span>;
                            }
                            return `${percentage.toFixed(2)}%`;
                          })()}
                        </td>
                        {isSuperuser && (
                          <td className="py-2 px-4 border-b text-center">
                            <div className="flex gap-2 justify-center flex-wrap">
                              <button
                                onClick={() => openEditUserModal(dailyEntry.user, userName, correspondingRecord?.designation?.id)}
                                className="bg-green-500 hover:bg-green-600 text-white px-3 py-1 rounded text-sm transition-colors"
                                title="Edit Details"
                              >
                                📝 Edit
                              </button>
                              <button
                                onClick={() => openUpdateTargetModal(dailyEntry.user, userName, target, targetRev)}
                                className="bg-blue-500 hover:bg-blue-600 text-white px-3 py-1 rounded text-sm transition-colors"
                                title="Update Target"
                              >
                                🎯 Target
                              </button>
                              <button
                                onClick={() => setDeleteConfirmUser({ id: dailyEntry.user, name: userName })}
                                className="bg-red-500 hover:bg-red-600 text-white px-3 py-1 rounded text-sm transition-colors"
                                title="Delete User"
                              >
                                🗑️ Delete
                              </button>
                            </div>
                          </td>
                        )}
                      </tr>
                      {/* Extended Row */}
                      {isExpanded && (
                        <tr>
                          <td colSpan={isSuperuser ? "11" : "10"} className="p-4 bg-gray-100">
                            <table
                              style={{
                                width: "100%",
                                borderCollapse: "collapse",
                              }}
                            >
                              <thead className="bg-gray-300">
                                <tr>
                                  <th className="py-2 px-4 border-b">
                                    Candidate Name
                                  </th>
                                  <th className="py-2 px-4 border-b">
                                    Hospital
                                  </th>
                                  <th className="py-2 px-4 border-b">City</th>
                                  <th className="py-2 px-4 border-b">State</th>
                                  <th className="py-2 px-4 border-b">
                                    Speciality
                                  </th>
                                  <th className="py-2 px-4 border-b">Status</th>
                                  <th className="py-2 px-4 border-b">
                                    Exp. Revenue
                                  </th>
                                  <th className="py-2 px-4 border-b">
                                    Revenue
                                  </th>
                                  <th className="py-2 px-4 border-b">
                                    Date
                                  </th>
                                </tr>
                              </thead>
                              <tbody>
                                {candidateRecords[dailyEntry.user]?.map(
                                  (record) => {
                                    const isNestedExpanded =
                                      nestedExpandedRows[dailyEntry.user] ===
                                      record.id;
                                    return (
                                      <React.Fragment key={record.id}>
                                        <tr
                                          onClick={() =>
                                            toggleNestedExpandedRow(
                                              dailyEntry.user,
                                              record.id
                                            )
                                          }
                                          style={{
                                            cursor: "pointer",
                                            backgroundColor: isNestedExpanded
                                              ? "#f0f8ff"
                                              : "transparent",
                                            padding: "35px",
                                          }}
                                        >
                                          <td className="py-4 px-4 border-b text-center">
                                            {record.candidate_name}
                                          </td>
                                          <td className="py-4 px-4 border-b text-center">
                                            {record.hospital}
                                          </td>
                                          <td className="py-4 px-4 border-b text-center">
                                            {record.city}
                                          </td>
                                          <td className="py-4 px-4 border-b text-center">
                                            {record.state}
                                          </td>
                                          <td className="py-4 px-4 border-b text-center">
                                            {record.speciality}
                                          </td>
                                          <td
                                            className="py-4 px-4 border-b text-center font-semibold"
                                            style={{
                                              color:
                                                record.status === "resume_forwarded"
                                                  ? "#10b981" // green
                                                  : record.status === "pipeline"
                                                    ? "#10b981" // green
                                                    : record.status === "turned_up"
                                                      ? "#f59e0b" // orange
                                                      : record.status === "backout"
                                                        ? "#ef4444" // red
                                                        : record.status === "not_selected"
                                                          ? "#6b7280" // gray
                                                          : record.status === "selected"
                                                            ? "#3b82f6" // blue
                                                            : record.status === "joined"
                                                              ? "#10b981" // green
                                                              : "#000000", // black
                                            }}
                                          >
                                            {record.status.replace(/_/g, ' ').toUpperCase()}
                                          </td>
                                          <td className="py-4 px-4 border-b text-center font-semibold">
                                            ₹{record.status === 'selected' ? parseFloat((parseFloat(record.expected_revenue) > 0 ? record.expected_revenue : record.revenue) || 0).toFixed(2) : '0.00'}L
                                          </td>
                                          <td className="py-4 px-4 border-b text-center font-semibold">
                                            ₹{record.status === 'joined' ? parseFloat(record.revenue || 0).toFixed(2) : '0.00'}L
                                          </td>
                                          <td className="py-4 px-4 border-b text-center text-sm text-gray-600">
                                            {new Date(record.date_entered).toLocaleDateString('en-IN', {
                                              day: '2-digit',
                                              month: 'short',
                                              year: 'numeric'
                                            })}
                                          </td>
                                        </tr>
                                        {/* Nested Expanded Row */}
                                        {isNestedExpanded && (
                                          <tr
                                            style={{
                                              backgroundColor: "#96DED1",
                                              border: "3px solid #f0f8ff",
                                              borderBottom: "2px solid 	#E8E8E8",
                                            }}
                                          >
                                            <td
                                              colSpan="11"
                                              className="p-3"
                                              style={{
                                                backgroundColor: "#96DED1",
                                              }}
                                            >
                                              <div>
                                                <strong>Remark:</strong>{" "}
                                                {record.remark}
                                              </div>
                                              <div>
                                                <strong>Date Entered:</strong>{" "}
                                                {record.date_entered}
                                              </div>
                                            </td>
                                          </tr>
                                        )}
                                      </React.Fragment>
                                    );
                                  }
                                )}
                              </tbody>
                            </table>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
                {/* Totals Row */}
                <tr className="bg-gray-300" style={{ fontSize: "17px" }}>
                  <td className="py-2 px-4 border-b text-center font-bold">
                    Total
                  </td>
                  <td className="py-2 px-4 border-b"></td>
                  <td className="py-2 px-4 border-b text-center font-bold">
                    {totalForwarded}
                  </td>
                  <td className="py-2 px-4 border-b text-center font-bold">
                    {totalPipeline}
                  </td>
                  <td className="py-2 px-4 border-b text-center font-bold">
                    {totalTurnedUp}
                  </td>
                  <td className="py-2 px-4 border-b text-center font-bold">
                    {totalSelected}
                  </td>
                  <td className="py-2 px-4 border-b text-center font-bold">
                    {totalJoined}
                  </td>
                  <td className="py-2 px-4 border-b text-center font-bold">
                    {totalBackout}
                  </td>
                  <td className="py-2 px-4 border-b text-center font-bold">
                    {totalNotSelected}
                  </td>
                  <td className="py-2 px-4 border-b text-center font-bold">
                    {displayMode === 'target_candidates'
                      ? `${totalExpectedRevenue.toFixed(2)} lac`
                      : `${totalExpectedRevenue.toFixed(2)} / ${totalTargetRevenue.toFixed(2)} lac`
                    }
                  </td>
                  <td className="py-2 px-4 border-b text-center font-bold ">
                    {displayMode === 'target_candidates' ? (
                      <>
                        <span
                          className={` ${totalPercentage >= 100
                            ? "text-blue-600"
                            : totalPercentage >= 95
                              ? "text-green-500"
                              : totalPercentage >= 85
                                ? "text-yellow-500"
                                : "text-red-500"
                            }`}
                        >
                          {totalSelected}{" "}
                        </span>
                        / {totalTargetCandidates}
                      </>
                    ) : (
                      <>
                        <span
                          className={` ${totalPercentage >= 100
                            ? "text-blue-600"
                            : totalPercentage >= 95
                              ? "text-green-500"
                              : totalPercentage >= 85
                                ? "text-yellow-500"
                                : "text-red-500"
                            }`}
                        >
                          {totalRevenue.toFixed(2)}{" "}
                        </span>
                        lac
                      </>
                    )}
                  </td>
                  <td
                    className={`py-2 px-4 border-b text-center font-bold ${totalPercentage >= 100
                      ? "text-blue-600"
                      : totalPercentage >= 95
                        ? "text-green-500"
                        : totalPercentage >= 85
                          ? "text-yellow-500"
                          : "text-red-500"
                      }`}
                  >
                    {totalPercentage.toFixed(2)}%
                  </td>
                  {isSuperuser && <td className="py-2 px-4 border-b"></td>}
                </tr>
              </tbody>
            </table>

            {/* Edit User Modal */}
            {editUser && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
                  <h3 className="text-xl font-bold text-green-600 mb-4">
                    📝 Edit Employee Details - {editUser.name}
                  </h3>
                  <div className="space-y-4 mb-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Name:
                      </label>
                      <input
                        type="text"
                        value={editName}
                        onChange={(e) => setEditName(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                        placeholder="Enter employee name"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Designation:
                      </label>
                      <select
                        value={editDesignation}
                        onChange={(e) => setEditDesignation(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                      >
                        <option value="">Select Designation</option>
                        {designations.map((des) => (
                          <option key={des.id} value={des.id}>
                            {des.name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Profile Image:
                      </label>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => setEditImage(e.target.files[0])}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                      />
                      <p className="text-xs text-gray-500 mt-1">Leave empty to keep current image</p>
                    </div>
                  </div>
                  <div className="flex justify-end gap-3">
                    <button
                      onClick={() => setEditUser(null)}
                      className="px-4 py-2 bg-gray-300 hover:bg-gray-400 text-gray-800 rounded transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleEditUser}
                      className="px-4 py-2 bg-green-500 hover:bg-green-600 text-white rounded transition-colors"
                      disabled={!editName}
                    >
                      Save Changes
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Delete Confirmation Modal */}
            {deleteConfirmUser && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
                  <h3 className="text-xl font-bold text-red-600 mb-4">
                    ⚠️ Confirm Deletion
                  </h3>
                  <p className="text-gray-700 mb-6">
                    Are you sure you want to delete user <strong>{deleteConfirmUser.name}</strong>?
                    <br />
                    <span className="text-red-500 text-sm">
                      This action cannot be undone. All associated data will be permanently deleted.
                    </span>
                  </p>
                  <div className="flex justify-end gap-3">
                    <button
                      onClick={() => setDeleteConfirmUser(null)}
                      className="px-4 py-2 bg-gray-300 hover:bg-gray-400 text-gray-800 rounded transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={() => handleDeleteUser(deleteConfirmUser.id)}
                      className="px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded transition-colors"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Update Target Modal */}
            {updateTargetUser && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
                  <h3 className="text-xl font-bold text-blue-600 mb-4">
                    🎯 Update Target for {updateTargetUser.name}
                  </h3>
                  <div className="space-y-4 mb-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Target (Selected Candidates):
                      </label>
                      <input
                        type="number"
                        value={targetInput}
                        onChange={(e) => setTargetInput(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="Enter target number"
                        min="0"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Revenue Target (in Lac):
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        value={revenueTargetInput}
                        onChange={(e) => setRevenueTargetInput(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="Enter revenue target"
                        min="0"
                        required
                      />
                    </div>
                  </div>
                  <div className="flex justify-end gap-3">
                    <button
                      onClick={() => setUpdateTargetUser(null)}
                      className="px-4 py-2 bg-gray-300 hover:bg-gray-400 text-gray-800 rounded transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleUpdateTarget}
                      className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded transition-colors"
                      disabled={!targetInput || !revenueTargetInput}
                    >
                      Update Target
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>
    </>
  );
}

export default Records;
